import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaExchangeAlt,
  FaArrowUp,
  FaArrowDown,
  FaCalendarAlt,
  FaDownload,
  FaFileExcel,
  FaFilter,
  FaUserTimes,
  FaUserCheck,
  FaRobot,
  FaBan,
  FaSave,
  FaChartBar,
  FaTachometerAlt,
  FaCheckCircle,
  FaDatabase
} from 'react-icons/fa';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import RecordUsageModal from '../../components/RecordUsageModal/RecordUsageModal';
import { GoldStar, SilverStar } from '../../components/PlanCard/PlanCard';
import { apiHelper } from '../../utils/api';
import useConfirm from '../../hooks/useConfirm';
import './Admin.scss';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    icon: <FaUser />,
    color: '#6366F1',
    price: 0,
    features: ['Basic features', 'Email support']
  },
  {
    id: 'silver',
    name: 'Silver',
    icon: <SilverStar />,
    color: '#9CA3AF',
    price: 999,
    features: ['All Free features', 'Priority support', 'Advanced analytics']
  },
  {
    id: 'gold',
    name: 'Gold',
    icon: <GoldStar />,
    color: '#F59E0B',
    price: 1999,
    features: ['All Silver features', '24/7 support', 'Custom branding']
  }
];

const PlanManagement = () => {
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandledRef = useRef(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // Plan change modal
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [updating, setUpdating] = useState(false);

  // Extend plan modal
  const [extendPlanUser, setExtendPlanUser] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extendMode, setExtendMode] = useState('add'); // 'add' | 'reduce'
  const [extendSaving, setExtendSaving] = useState(false);
  const [extendRefreshing, setExtendRefreshing] = useState(false);
  const [extendNow, setExtendNow] = useState(Date.now());

  // Stats
  const [stats, setStats] = useState({
    free: 0,
    silver: 0,
    gold: 0
  });

  // AI Settings modal
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiUser, setAiUser] = useState(null);
  const [aiSettings, setAiSettings] = useState(null);
  const [aiSettingsLoading, setAiSettingsLoading] = useState(false);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [aiBlocked, setAiBlocked] = useState(false);
  const [aiDailyLimit, setAiDailyLimit] = useState('');
  const [aiRateLimit, setAiRateLimit] = useState('');
  const [globalDefaults, setGlobalDefaults] = useState({ dailyLimit: 1000, rateLimitPerMinute: 15 });
  const [planPrices, setPlanPrices] = useState({});

  // Image preview
  const [previewImage, setPreviewImage] = useState(null);

  // Record usage modal (same as Admin Security user log)
  const [recordUsageUser, setRecordUsageUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(selectedPlan && { plan: selectedPlan }),
        ...(showExpiredOnly && { expired: 'true' }),
        ...(roleFilter && { designation: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      });

      const data = await apiHelper.get(`/admin/users?${params.toString()}`);
      setUsers(data.users);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, selectedPlan, showExpiredOnly, roleFilter, statusFilter]);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await apiHelper.get('/admin/users/designations');
      setAvailableRoles(data.designations || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, []);

  const fetchPlanPrices = async () => {
    try {
      const data = await apiHelper.get('/admin/plan-settings');
      if (data?.plans) {
        const prices = {};
        data.plans.forEach(p => {
          const yearlyPrice = p.discPrice365 || p.basePrice365 || p.price;
          const taxRate = p.taxPercentage || 0;
          prices[p.planId] = Math.round(yearlyPrice * (1 + taxRate / 100));
        });
        setPlanPrices(prices);
      }
    } catch (err) {
      console.error('Error fetching plan prices:', err);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await apiHelper.patch('/admin/users/status', { userId, status: newStatus });
      fetchUsers();
    } catch (err) {
      console.error('Error updating status:', err);
      await confirm('Failed to update user status. Please try again.', {
        title: 'Error',
        confirmText: 'OK',
        cancelText: ''
      });
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiHelper.get('/admin/metrics');
      setStats({
        free: data.freeUsers || 0,
        silver: data.silverUsers || 0,
        gold: data.goldUsers || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    fetchPlanPrices();
  }, []);

  // Debounced search - reset to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [selectedPlan, showExpiredOnly, roleFilter, statusFilter]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handlePlanChange = async () => {
    if (!selectedUser || !newPlan) return;
    
    const currentPlan = selectedUser.plan || 'free';
    const currentPlanInfo = getPlanInfo(currentPlan);
    const newPlanInfo = getPlanInfo(newPlan);
    
    // Determine if upgrade or downgrade
    const currentIndex = PLANS.findIndex(p => p.id === currentPlan);
    const newIndex = PLANS.findIndex(p => p.id === newPlan);
    
    let confirmMessage = '';
    let confirmTitle = '';
    
    if (newIndex > currentIndex) {
      // Upgrade
      confirmTitle = 'Confirm Plan Upgrade';
      confirmMessage = `Are you sure you want to upgrade ${selectedUser.name || 'this user'}'s plan from ${currentPlanInfo.name} to ${newPlanInfo.name}?`;
    } else if (newIndex < currentIndex) {
      // Downgrade
      confirmTitle = 'Confirm Plan Downgrade';
      confirmMessage = `Are you sure you want to downgrade ${selectedUser.name || 'this user'}'s plan to ${newPlanInfo.name}?`;
    } else {
      // Same plan (shouldn't happen due to button disable, but just in case)
      confirmTitle = 'No Change';
      confirmMessage = `This user is already on the ${currentPlanInfo.name} plan.`;
    }
    
    try {
      const confirmed = await confirm(confirmMessage, {
        title: confirmTitle,
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });
      
      if (!confirmed) return;
      
      setUpdating(true);
      await apiHelper.patch('/admin/users/plan', { 
        userId: selectedUser._id, 
        plan: newPlan 
      });
      
      fetchUsers();
      fetchStats();
      setShowPlanModal(false);
      setSelectedUser(null);
      setNewPlan('');
    } catch (err) {
      console.error('Error updating plan:', err);
      // Show error confirmation
      await confirm('Failed to update plan. Please try again.', {
        title: 'Error',
        confirmText: 'OK',
        cancelText: ''
      });
    } finally {
      setUpdating(false);
    }
  };

  const openPlanModal = (user) => {
    setSelectedUser(user);
    setNewPlan(user.plan || 'free');
    setShowPlanModal(true);
  };

  // Deep link from Admin Dashboard "Recent Signups": open the change-plan
  // modal for the requested user (navigated with location.state).
  useEffect(() => {
    const pendingId = location.state?.openPlanUserId;
    const pendingUser = location.state?.openPlanUser;
    if (!pendingId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    // Clear the navigation state so back/refresh doesn't reopen the modal
    navigate(location.pathname, { replace: true, state: {} });

    const openFromListOrFetch = async () => {
      try {
        // Prefer the already-loaded row (has plan/expiry snapshot fields)
        const fromList = users.find((u) => String(u._id) === String(pendingId));
        if (fromList) {
          openPlanModal(fromList);
          return;
        }
        // Fallback to the lightweight dashboard payload
        if (pendingUser && String(pendingUser._id) === String(pendingId)) {
          try {
            const data = await apiHelper.get(`/admin/users/${pendingId}`);
            if (data?.user) {
              openPlanModal(data.user);
              return;
            }
          } catch {
            // fall through to opening with the dashboard payload
          }
          openPlanModal(pendingUser);
          return;
        }
        const data = await apiHelper.get(`/admin/users/${pendingId}`);
        if (data?.user) openPlanModal(data.user);
      } catch (err) {
        console.error('Error opening plan modal from dashboard:', err);
      }
    };
    openFromListOrFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleExtendPlan = async () => {
    if (!extendPlanUser) return;
    const days = parseInt(extendDays, 10);
    if (isNaN(days) || days === 0) return;
    const finalDays = extendMode === 'reduce' ? -days : days;
    setExtendSaving(true);
    try {
      await apiHelper.patch('/admin/users/extend-plan', { userId: extendPlanUser._id, days: finalDays });
      fetchUsers();
      setExtendPlanUser(null);
      setExtendDays(30);
      setExtendMode('add');
    } catch (err) {
      console.error('Error extending plan:', err);
      await confirm('Failed to update plan days. Please try again.', {
        title: 'Error',
        confirmText: 'OK',
        cancelText: ''
      });
    } finally {
      setExtendSaving(false);
    }
  };

  const handleResetPlanAdjustment = async () => {
    if (!extendPlanUser || !extendPlanUser.planExtraDays) return;

    setExtendSaving(true);
    try {
      await apiHelper.patch('/admin/users/extend-plan', {
        userId: extendPlanUser._id,
        reset: true,
      });
      fetchUsers();
      setExtendPlanUser(null);
      setExtendDays(30);
      setExtendMode('add');
    } catch (err) {
      console.error('Error resetting plan adjustment:', err);
      await confirm('Failed to reset plan days. Please try again.', {
        title: 'Error',
        confirmText: 'OK',
        cancelText: ''
      });
    } finally {
      setExtendSaving(false);
    }
  };

  // Fetch real-time plan data so the extend modal always reflects the
  // latest subscription state from the server.
  const fetchExtendUserData = useCallback(async (userId) => {
    if (!userId) return;
    setExtendRefreshing(true);
    try {
      const data = await apiHelper.get(`/admin/users/${userId}`);
      if (data?.user) {
        setExtendPlanUser(prev => (prev && prev._id === userId ? { ...prev, ...data.user } : prev));
        setExtendNow(Date.now());
      }
    } catch (err) {
      console.error('Error fetching plan data:', err);
    } finally {
      setExtendRefreshing(false);
    }
  }, []);

  const openExtendModal = (user) => {
    setExtendDays(30);
    setExtendMode('add');
    setExtendNow(Date.now());
    setExtendPlanUser(user);
    fetchExtendUserData(user._id);
  };

  // While the extend modal is open, keep its data live: re-sync with the
  // server periodically and tick a local clock for the countdown.
  useEffect(() => {
    if (!extendPlanUser?._id) return undefined;
    const userId = extendPlanUser._id;
    const tickTimer = setInterval(() => setExtendNow(Date.now()), 30000);
    const syncTimer = setInterval(() => fetchExtendUserData(userId), 60000);
    return () => {
      clearInterval(tickTimer);
      clearInterval(syncTimer);
    };
  }, [extendPlanUser?._id, fetchExtendUserData]);

  const openAiModal = async (user, e) => {
    if (e) e.stopPropagation();
    setAiUser(user);
    setShowAiModal(true);
    setAiSettingsLoading(true);
    try {
      const data = await apiHelper.get(`/admin/users/${user._id}/ai-settings`);
      setAiSettings(data);
      setAiBlocked(data.aiAssistant?.isBlocked || false);
      setAiDailyLimit(data.aiAssistant?.dailyLimit ?? '');
      setAiRateLimit(data.aiAssistant?.rateLimitPerMinute ?? '');
      setGlobalDefaults(data.globalDefaults || { dailyLimit: 1000, rateLimitPerMinute: 15 });
    } catch (err) {
      console.error('Error fetching AI settings:', err);
    } finally {
      setAiSettingsLoading(false);
    }
  };

  const handleSaveAiSettings = async () => {
    if (!aiUser) return;
    setAiSettingsSaving(true);
    try {
      const payload = {
        isBlocked: aiBlocked,
        dailyLimit: aiDailyLimit === '' ? null : parseInt(aiDailyLimit, 10),
        rateLimitPerMinute: aiRateLimit === '' ? null : parseInt(aiRateLimit, 10),
      };
      await apiHelper.put(`/admin/users/${aiUser._id}/ai-settings`, payload);
      setShowAiModal(false);
      setAiUser(null);
      setAiSettings(null);
    } catch (err) {
      console.error('Error saving AI settings:', err);
      await confirm('Failed to save AI settings. Please try again.', {
        title: 'Error',
        confirmText: 'OK',
        cancelText: ''
      });
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getJoinedSource = (user) => user.createdAt || user.created_at || user.joinedAt || user.joined || null;

  const getActivatedSource = (user) => user.planActivatedAt || user.createdAt || user.created_at || user.joinedAt || user.joined || null;

  const formatJoinedDate = (user) => {
    // Show user registration/creation date
    const joinedDate = getJoinedSource(user);

    if (!joinedDate) return '-';

    try {
      return new Date(joinedDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (err) {
      return '-';
    }
  };

  const formatPlanDate = (user) => {
    // Show plan activation date for paid plans, otherwise registration date
    const planDate = getActivatedSource(user);

    if (!planDate) return '';

    try {
      return new Date(planDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (err) {
      return '';
    }
  };

  // Time-of-day part (e.g. "2:30 pm") shown under the date in the table so
  // admins can see the exact timestamp a plan starts/expires at.
  const formatTimeOnly = (dateValue) => {
    if (!dateValue) return '';
    try {
      const parsed = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleTimeString('en-IN', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    } catch (err) {
      return '';
    }
  };

  const formatFullDateTime = (dateValue) => {
    if (!dateValue) return '';
    try {
      const parsed = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return '';
      return `${parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })}, ${formatTimeOnly(parsed)}`;
    } catch (err) {
      return '';
    }
  };

  const getPlanExpiryDate = (user) => {
    // Prefer server-computed canonical expiry (same as SignupPlanControl Recent Registers).
    // Backend: backend/utils/subscription.js#getSubscriptionStatus
    //   duration = (initialPlanDays ?? 15/365) + planExtraDays
    const serverExpires = user?.subscription?.expiresAt || user?.planExpiresAt;
    if (serverExpires) {
      try {
        const parsed = new Date(serverExpires);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      } catch (err) {
        // fall through to local fallback
      }
    }

    const plan = String(user?.plan || 'free').toLowerCase();

    const planDate = user.planActivatedAt || user.createdAt || user.created_at || user.joinedAt || user.joined;
    if (!planDate) return null;

    try {
      const activationDate = new Date(planDate);
      const expiryDate = new Date(activationDate);

      const extraDays = parseInt(user.planExtraDays || 0, 10) || 0;
      // Signup-plan snapshot assigned at registration; legacy users have null -> 15/365 fallback.
      const snapshotDays = parseInt(user?.initialPlanDays, 10);
      const baseDays = Number.isFinite(snapshotDays) && snapshotDays > 0
        ? snapshotDays
        : (plan === 'silver' || plan === 'gold' ? 365 : 15);
      expiryDate.setDate(expiryDate.getDate() + baseDays + extraDays);

      return expiryDate;
    } catch (err) {
      return null;
    }
  };

  const formatPlanExpiry = (user) => {
    const expiryDate = getPlanExpiryDate(user);
    if (!expiryDate) {
      return '-';
    }
    
    return expiryDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isPlanExpired = (user) => {
    // Prefer server-computed flags (same source as Recent Registers + expired filter)
    if (typeof user?.subscription?.isExpired === 'boolean') return user.subscription.isExpired;
    if (typeof user?.planIsExpired === 'boolean') return user.planIsExpired;

    const expiryDate = getPlanExpiryDate(user);
    if (!expiryDate) return false;

    return new Date() > expiryDate;
  };

  // Real-time expiry info for the extend modal. Prefers the server-computed
  // subscription snapshot and falls back to local calculation.
  const getExtendExpiryInfo = () => {
    if (!extendPlanUser) return null;
    const sub = extendPlanUser.subscription || {};

    let expiresAtMs = null;
    if (extendPlanUser.planExpiresAt) {
      const parsed = new Date(extendPlanUser.planExpiresAt).getTime();
      if (!Number.isNaN(parsed)) expiresAtMs = parsed;
    }
    if (expiresAtMs === null) {
      const fallback = getPlanExpiryDate(extendPlanUser);
      if (fallback && !Number.isNaN(fallback.getTime())) expiresAtMs = fallback.getTime();
    }

    if (expiresAtMs === null) {
      return { neverExpires: true, expired: false, expiresAtMs: null };
    }

    const msLeft = expiresAtMs - extendNow;
    const expired = typeof sub.isExpired === 'boolean' ? sub.isExpired : msLeft <= 0;
    const totalMinutes = Math.floor(Math.abs(msLeft) / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const remainingLabel = msLeft <= 0
      ? (days > 0 ? `${days}d ${hours}h ago`
        : hours > 0 ? `${hours}h ${minutes}m ago`
          : `${minutes}m ago`)
      : (days > 0 ? `${days}d ${hours}h left`
        : hours > 0 ? `${hours}h ${minutes}m left`
          : `${minutes}m left`);

    return { neverExpires: false, expired, expiresAtMs, remainingLabel };
  };

  // Projected expiry date if the currently selected extend/reduce is applied.
  const getExtendPreviewLabel = (expiryInfo) => {
    if (!expiryInfo || expiryInfo.neverExpires || expiryInfo.expiresAtMs === null) return null;
    const days = parseInt(extendDays, 10);
    if (!days || Number.isNaN(days)) return null;
    const delta = extendMode === 'reduce' ? -days : days;
    const projected = new Date(expiryInfo.expiresAtMs + delta * MS_PER_DAY);
    if (Number.isNaN(projected.getTime())) return null;
    return projected.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'gold': return <GoldStar />;
      case 'silver': return <SilverStar />;
      default: return <FaUser />;
    }
  };

  const handleExport = async (format = 'json') => {
    try {
      const confirmed = await confirm(`Export users as ${String(format).toUpperCase()}?`, {
        title: 'Export Users',
        confirmText: 'Export',
        cancelText: 'Cancel',
        showWarning: false
      });
      if (!confirmed) return;
      // Build params with current filters
      const params = new URLSearchParams({
        type: 'users',
        ...(search && { search }),
        ...(selectedPlan && { plan: selectedPlan }),
        ...(showExpiredOnly && { expired: 'true' }),
        ...(roleFilter && { designation: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      });
      
      const data = await apiHelper.get(`/admin/export?${params.toString()}`);
      const users = data.data;
      const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
      
      if (format === 'csv') {
        // Convert to CSV with plan focus - properly escaped + BOM for Excel
        const headers = ['Name', 'Email', 'Role', 'Plan', 'Status', 'Joined (Date & Time)', 'Activated (Date & Time)', 'Expires (Date & Time)', 'Extended/Reduced Days', 'Expired'];
        const csvRows = [
          headers.map(csvEscape).join(','),
          ...users.map(user => {
            const userPlan = PLANS.find(p => p.id === (user.plan || 'free')) || PLANS[0];
            const isExpired = isPlanExpired(user);
            return [
              user.name || 'Unnamed',
              user.email || '',
              user.designation || 'Unknown',
              userPlan.name,
              user.status || '',
              formatFullDateTime(getJoinedSource(user)) || '-',
              formatFullDateTime(getActivatedSource(user)),
              formatFullDateTime(getPlanExpiryDate(user)) || '-',
              user.planExtraDays ? (user.planExtraDays > 0 ? `+${user.planExtraDays}` : String(user.planExtraDays)) : '0',
              isExpired ? 'Yes' : 'No'
            ].map(csvEscape).join(',');
          })
        ];
        const csvContent = csvRows.join('\r\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, `plan_users_export_${new Date().toISOString().split('T')[0]}.csv`);
      } else {
        // JSON export
        const jsonContent = JSON.stringify(
          users.map((user) => ({
            ...user,
            role: user.designation || 'Unknown',
            joinedDate: formatFullDateTime(getJoinedSource(user)) || '-',
            planActivated: formatFullDateTime(getActivatedSource(user)),
            planExpires: formatFullDateTime(getPlanExpiryDate(user)) || '-',
            extendedDays: user.planExtraDays ? (user.planExtraDays > 0 ? `+${user.planExtraDays}` : String(user.planExtraDays)) : '0',
            isExpired: isPlanExpired(user)
          })),
          null,
          2
        );
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        downloadBlob(blob, `plan_users_export_${new Date().toISOString().split('T')[0]}.json`);
      }
    } catch (err) {
      console.error('Error exporting users:', err);
      await confirm('Failed to export users. Please try again.', {
        title: 'Export Error',
        confirmText: 'OK',
        cancelText: ''
      });
    }
  };

  const getPlanInfo = (planId) => {
    return PLANS.find(p => p.id === planId) || PLANS[0];
  };

  const getPlanYearlyPrice = (planId) => {
    if (planPrices[planId] !== undefined) return planPrices[planId];
    const plan = PLANS.find(p => p.id === planId);
    return plan ? plan.price * 12 : 0;
  };

  const extendExpiryInfo = extendPlanUser ? getExtendExpiryInfo() : null;
  const extendPreviewDate = getExtendPreviewLabel(extendExpiryInfo);
  const formatExpiryDay = (ms) => new Date(ms).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  return (
    <div className="admin-plan-management">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-page-header__left">
          <h2>User Plan Management</h2>
          <p>Manage user subscriptions and plan upgrades</p>
        </div>
        <div className="admin-page-header__right">
          <div className="admin-export-btns">
            <button 
              className="admin-btn admin-btn--outline admin-btn--small"
              onClick={() => handleExport('csv')}
              title="Export as CSV"
            >
              <FaFileExcel /> Export CSV
            </button>
            <button 
              className="admin-btn admin-btn--outline admin-btn--small"
              onClick={() => handleExport('json')}
              title="Export as JSON"
            >
              <FaDownload /> Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Plan Stats Cards */}
      <div className="admin-plan-stats">
        {PLANS.map((plan) => (
          <div 
            key={plan.id}
            className={`admin-plan-stat-card admin-plan-stat-card--${plan.id} ${selectedPlan === plan.id ? 'admin-plan-stat-card--selected' : ''}`}
            onClick={() => {
              setSelectedPlan(selectedPlan === plan.id ? '' : plan.id);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <div className="admin-plan-stat-card__icon" style={{ color: plan.color }}>
              {plan.icon}
            </div>
            <div className="admin-plan-stat-card__content">
              <span className="admin-plan-stat-card__count">{stats[plan.id]}</span>
              <span className="admin-plan-stat-card__label">{plan.name} Users</span>
            </div>
            <div className="admin-plan-stat-card__price">
              {plan.price > 0 ? `₹${getPlanYearlyPrice(plan.id).toLocaleString('en-IN')}/yr` : 'Free'}
            </div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="admin-toolbar">
        <div className="admin-search">
          <FaSearch className="admin-search__icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-search__input"
          />
          {search && (
            <button className="admin-search__clear" onClick={() => setSearch('')}>
              <FaTimes />
            </button>
          )}
        </div>
        <button 
          className={`admin-filter-btn ${showFilters ? 'admin-filter-btn--active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          <span>Filters</span>
          {(statusFilter || roleFilter || showExpiredOnly) && <span className="admin-filter-badge" />}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>Status</label>
            <CustomSelect
              value={statusFilter}
              onChange={(val) => {
                setStatusFilter(val);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </div>
          <div className="admin-filter-group">
            <label>Role</label>
            <CustomSelect
              value={roleFilter}
              onChange={(val) => {
                setRoleFilter(val);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              options={[
                { value: '', label: 'All Roles' },
                ...availableRoles.map((role) => ({ value: role, label: role })),
              ]}
            />
          </div>
          <div className="admin-filter-group">
            <label className="admin-filter-group__label">Expired</label>
            <button
              type="button"
              className={`admin-filter-toggle ${showExpiredOnly ? 'admin-filter-toggle--active' : ''}`}
              onClick={() => {
                setShowExpiredOnly(prev => !prev);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <span className="admin-filter-toggle__dot" />
              Expired Plans Only
            </button>
          </div>
          {(statusFilter || roleFilter || showExpiredOnly) && (
            <button
              className="admin-btn admin-btn--text"
              onClick={() => {
                setStatusFilter('');
                setRoleFilter('');
                setShowExpiredOnly(false);
              }}
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {/* Users Table */}
      <div className="admin-table-container">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading__spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="admin-error">
            <p>{error}</p>
            <button onClick={fetchUsers} className="admin-btn admin-btn--primary">
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="admin-empty-state">
            <div className="admin-empty-state__icon">
              <GoldStar />
            </div>
            <h3>No users found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Activated</th>
                <th>Expires</th>
                <th>Extended</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const userPlan = getPlanInfo(user.plan || 'free');
                const isExpired = isPlanExpired(user);
                const expiryDateObj = getPlanExpiryDate(user);
                const daysLeft = expiryDateObj
                  ? Math.ceil((expiryDateObj.getTime() - Date.now()) / MS_PER_DAY)
                  : null;
                const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 7;
                return (
                  <tr key={user._id} onClick={() => openPlanModal(user)} className={`admin-plan-row${isExpired ? ' admin-plan-row--expired' : isExpiringSoon ? ' admin-plan-row--soon' : ''}`} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="admin-user-cell">
                        <div
                          className={`admin-user-cell__avatar${user.photoURL ? ' admin-user-cell__avatar--clickable' : ''}`}
                          onClick={user.photoURL ? (e) => { e.stopPropagation(); setPreviewImage(user.photoURL); } : undefined}
                          title={user.photoURL ? 'Click to view photo' : undefined}
                        >
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.name} />
                          ) : (
                            <span>{user.name?.charAt(0) || user.email?.charAt(0) || '?'}</span>
                          )}
                        </div>
                        <div className="admin-user-cell__info">
                          <span className="admin-user-cell__name">{user.name || 'Unnamed User'}</span>
                          <span className="admin-user-cell__email">{user.email}</span>
                          <span className="admin-user-cell__role">{user.designation || 'Unknown'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={`admin-plan-badge admin-plan-badge--${user.plan || 'free'}`}>
                        {getPlanIcon(user.plan)}
                        <span>{userPlan.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`admin-status-badge admin-status-badge--${user.status || 'active'}`}>
                        {user.status || 'active'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-datetime-cell" title={formatFullDateTime(getJoinedSource(user))}>
                        <span className="admin-datetime-cell__date">{formatJoinedDate(user)}</span>
                        {formatTimeOnly(getJoinedSource(user)) && (
                          <span className="admin-datetime-cell__time">{formatTimeOnly(getJoinedSource(user))}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="admin-datetime-cell" title={formatFullDateTime(getActivatedSource(user))}>
                        <span className="admin-datetime-cell__date">{formatPlanDate(user)}</span>
                        {formatTimeOnly(getActivatedSource(user)) && (
                          <span className="admin-datetime-cell__time">{formatTimeOnly(getActivatedSource(user))}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={`admin-expiry${isExpired ? ' admin-expiry--expired' : isExpiringSoon ? ' admin-expiry--soon' : ''}`} title={expiryDateObj ? formatFullDateTime(expiryDateObj) : ''}>
                        <span className="admin-expiry__date"><FaCalendarAlt className="admin-expiry__icon" />{formatPlanExpiry(user)}</span>
                        {expiryDateObj && formatTimeOnly(expiryDateObj) && (
                          <span className="admin-expiry__time">{formatTimeOnly(expiryDateObj)}</span>
                        )}
                        {isExpired ? (
                          <span className="admin-expiry__pill admin-expiry__pill--expired">Expired{daysLeft !== null && daysLeft < 0 ? ` ${Math.abs(daysLeft)}d ago` : ''}</span>
                        ) : daysLeft !== null ? (
                          <span className={`admin-expiry__pill${isExpiringSoon ? ' admin-expiry__pill--soon' : ''}`}>{daysLeft}d left</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      {(user.planExtraDays && user.planExtraDays !== 0) ? (
                        <span className={`admin-extended-days admin-extended-days--${user.planExtraDays > 0 ? 'plus' : 'minus'}`}>
                          {user.planExtraDays > 0 ? '+' : ''}{user.planExtraDays}d
                        </span>
                      ) : (
                        <span className="admin-extended-days admin-extended-days--none">—</span>
                      )}
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        <button 
                          className="admin-btn admin-btn--outline admin-btn--small"
                          onClick={(e) => { e.stopPropagation(); openPlanModal(user); }}
                        >
                          <FaExchangeAlt />
                          Plan
                        </button>
                        <button 
                          className="admin-btn admin-btn--outline admin-btn--small"
                          onClick={(e) => { e.stopPropagation(); openExtendModal(user); }}
                          title="Extend plan days"
                        >
                          <FaCalendarAlt />
                          Extend
                        </button>
                        <button 
                          className="admin-btn admin-btn--outline admin-btn--small"
                          onClick={(e) => openAiModal(user, e)}
                          title="AI assistant settings"
                        >
                          <FaRobot />
                          AI
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="admin-pagination">
          <div className="admin-pagination__info">
            <span>Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results</span>
          </div>
          
          <div className="admin-pagination__controls">
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || loading}
              onClick={() => handlePageChange(1)}
              title="First Page"
            >
              First
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || loading}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <FaChevronLeft /> Previous
            </button>
            
            <span className="admin-pagination__page-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || loading}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next <FaChevronRight />
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || loading}
              onClick={() => handlePageChange(pagination.pages)}
              title="Last Page"
            >
              Last
            </button>
          </div>

          <div className="admin-pagination__size">
            <label>Per page:</label>
            <CustomSelect
              value={pagination.limit}
              onChange={handleLimitChange}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              disabled={loading}
            />
          </div>
        </div>
      )}

      {/* Extend Plan Modal */}
      {extendPlanUser && (
        <div className="admin-modal-overlay" onClick={() => setExtendPlanUser(null)}>
          <div className="admin-modal admin-modal--small" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h3>{extendMode === 'reduce' ? 'Reduce Plan Days' : 'Extend Plan Days'}</h3>
              <button className="admin-modal__close" onClick={() => setExtendPlanUser(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal__body">
              <div className="extend-plan-user">
                <div className="admin-user-cell__avatar">
                  {extendPlanUser.photoURL
                    ? <img src={extendPlanUser.photoURL} alt={extendPlanUser.name} />
                    : <span>{extendPlanUser.name?.charAt(0) || '?'}</span>}
                </div>
                <div>
                  <strong>{extendPlanUser.name || 'Unnamed User'}</strong>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>{extendPlanUser.email}</p>
                  <div className={`admin-plan-badge admin-plan-badge--${extendPlanUser.plan}`} style={{ marginTop: '0.375rem' }}>
                    {getPlanIcon(extendPlanUser.plan)}
                    <span>{extendPlanUser.plan}</span>
                  </div>
                </div>
              </div>
              <div className="extend-plan-expiry">
                <div className="extend-plan-expiry__header">
                  <span>Live Plan Status</span>
                  <span className={`extend-plan-live${extendRefreshing ? ' extend-plan-live--syncing' : ''}`}>
                    <span className="extend-plan-live__dot" />
                    {extendRefreshing ? 'Syncing…' : 'Live'}
                  </span>
                </div>
                {extendExpiryInfo?.neverExpires ? (
                  <div className="extend-plan-expiry__grid">
                    <span className="extend-plan-expiry__label">Expiry</span>
                    <strong className="extend-plan-expiry__value">Never expires</strong>
                  </div>
                ) : extendExpiryInfo ? (
                  <>
                    <div className="extend-plan-expiry__grid">
                      <span className="extend-plan-expiry__label">Expires on</span>
                      <strong className="extend-plan-expiry__value">{formatExpiryDay(extendExpiryInfo.expiresAtMs)}</strong>
                    </div>
                    <div className="extend-plan-expiry__grid">
                      <span className="extend-plan-expiry__label">{extendExpiryInfo.expired ? 'Expired' : 'Time remaining'}</span>
                      <strong
                        className={`extend-plan-expiry__value ${extendExpiryInfo.expired ? 'extend-plan-expiry__value--expired' : 'extend-plan-expiry__value--active'}`}
                      >
                        {extendExpiryInfo.remainingLabel}
                      </strong>
                    </div>
                    {(extendPlanUser.planExtraDays ?? 0) !== 0 && (
                      <div className="extend-plan-expiry__grid">
                        <span className="extend-plan-expiry__label">Adjusted</span>
                        <span
                          className={`extend-plan-adjusted ${extendPlanUser.planExtraDays > 0 ? 'extend-plan-adjusted--plus' : 'extend-plan-adjusted--minus'}`}
                        >
                          {extendPlanUser.planExtraDays > 0 ? '+' : ''}{extendPlanUser.planExtraDays} day{Math.abs(extendPlanUser.planExtraDays) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    {extendPreviewDate && (
                      <div className="extend-plan-preview">
                        New expiry after this change: <strong>{extendPreviewDate}</strong>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              <div className="extend-plan-days-row">
                <div className="extend-plan-mode-toggle">
                  <button
                    type="button"
                    className={`extend-plan-mode-btn ${extendMode === 'add' ? 'active' : ''}`}
                    onClick={() => setExtendMode('add')}
                  >
                    + Add Days
                  </button>
                  <button
                    type="button"
                    className={`extend-plan-mode-btn extend-plan-mode-btn--reduce ${extendMode === 'reduce' ? 'active' : ''}`}
                    onClick={() => setExtendMode('reduce')}
                  >
                    − Reduce Days
                  </button>
                </div>
                <label>{extendMode === 'reduce' ? 'Days to remove' : 'Days to add'}</label>
                <div className="extend-plan-days-input">
                  <button type="button" onClick={() => setExtendDays(d => Math.max(1, d - 1))}>−</button>
                  <input
                    type="number"
                    min="1"
                    max="3650"
                    value={extendDays}
                    onChange={(e) => setExtendDays(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                  <button type="button" onClick={() => setExtendDays(d => Math.min(3650, d + 1))}>+</button>
                  <button
                    type="button"
                    className="extend-plan-reset-btn"
                    onClick={handleResetPlanAdjustment}
                    disabled={extendSaving || !extendPlanUser?.planExtraDays}
                    title={extendPlanUser?.planExtraDays ? 'Reset existing extra/reduced days to 0' : 'No extra or reduced days to reset'}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button className="admin-btn admin-btn--secondary" onClick={() => setExtendPlanUser(null)}>
                Cancel
              </button>
              <button
                className={`admin-btn ${extendMode === 'reduce' ? 'admin-btn--danger' : 'admin-btn--primary'}`}
                onClick={handleExtendPlan}
                disabled={extendSaving || !extendDays}
              >
                {extendSaving
                  ? 'Saving…'
                  : extendMode === 'reduce'
                    ? `Remove ${extendDays} day${extendDays !== 1 ? 's' : ''}`
                    : `Add ${extendDays} day${extendDays !== 1 ? 's' : ''}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Settings Modal */}
      {showAiModal && aiUser && (
        <div className="admin-modal-overlay" onClick={() => { setShowAiModal(false); setAiUser(null); setAiSettings(null); }}>
          <div className="admin-modal admin-modal--ai" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-brand-bar" />
            <div className="admin-modal__header">
              <div className="ai-modal-header-left">
                <div className="ai-modal-header-icon">
                  <FaRobot />
                </div>
                <div>
                  <h3>AI Assistant Settings</h3>
                  <p className="ai-modal-header-sub">Per-user controls for the AI assistant</p>
                </div>
              </div>
              <button className="admin-modal__close" onClick={() => { setShowAiModal(false); setAiUser(null); setAiSettings(null); }}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal__body">
              {aiSettingsLoading ? (
                <div className="admin-loading" style={{ padding: '3rem 0' }}>
                  <div className="admin-loading__spinner"></div>
                  <p>Loading AI settings...</p>
                </div>
              ) : (
                <>
                  {/* User Info Card */}
                  <div className="ai-modal-user-card">
                    <div className="ai-modal-user-avatar">
                      {aiUser.photoURL
                        ? <img src={aiUser.photoURL} alt={aiUser.name} />
                        : <span>{aiUser.name?.charAt(0) || aiUser.email?.charAt(0) || '?'}</span>}
                    </div>
                    <div className="ai-modal-user-info">
                      <strong className="ai-modal-user-name">{aiUser.name || 'Unnamed User'}</strong>
                      <span className="ai-modal-user-email">{aiUser.email}</span>
                      <span className={`ai-modal-user-plan-badge ai-modal-user-plan-badge--${aiUser.plan || 'free'}`}>
                        {getPlanIcon(aiUser.plan)}
                        {getPlanInfo(aiUser.plan || 'free').name}
                      </span>
                    </div>
                  </div>

                  {/* Usage Stats Cards */}
                  {aiSettings && (
                    <div className="ai-modal-section">
                      <div className="ai-modal-section-title">
                        <span className="ai-modal-section-dot" />
                        Current Usage
                      </div>
                      <div className="ai-modal-usage-grid">
                        <div className="ai-modal-usage-card">
                          <div className="ai-modal-usage-card-icon ai-modal-usage-card-icon--primary">
                            <FaRobot />
                          </div>
                          <div className="ai-modal-usage-card-value">{aiSettings.dailyAiUsage?.count || 0}</div>
                          <div className="ai-modal-usage-card-label">Queries Today</div>
                        </div>
                        <div className="ai-modal-usage-card">
                          <div className="ai-modal-usage-card-icon ai-modal-usage-card-icon--info">
                            <FaCalendarAlt />
                          </div>
                          <div className="ai-modal-usage-card-value ai-modal-usage-card-value--date">
                            {aiSettings.dailyAiUsage?.date
                              ? (() => {
                                  try {
                                    const d = new Date(aiSettings.dailyAiUsage.date);
                                    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                                  } catch { return aiSettings.dailyAiUsage.date; }
                                })()
                              : '—'}
                          </div>
                          <div className="ai-modal-usage-card-label">Last Active</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Access Control */}
                  <div className="ai-modal-section">
                    <div className="ai-modal-section-title">
                      <span className="ai-modal-section-dot" />
                      Access Control
                    </div>
                    <div className={`ai-modal-block-card ${aiBlocked ? 'ai-modal-block-card--blocked' : ''}`}>
                      <div className="ai-modal-block-card-left">
                        <div className="ai-modal-block-card-icon">
                          {aiBlocked ? <FaBan /> : <FaRobot />}
                        </div>
                        <div className="ai-modal-block-card-text">
                          <strong>{aiBlocked ? 'AI Access Blocked' : 'Allow AI Access'}</strong>
                          <p>{aiBlocked
                            ? 'This user cannot send queries to the AI assistant.'
                            : 'User can send queries to the AI assistant (subject to limits).'}</p>
                        </div>
                      </div>
                      <label className="ai-modal-toggle">
                        <input
                          type="checkbox"
                          checked={!aiBlocked}
                          onChange={(e) => setAiBlocked(!e.target.checked)}
                        />
                        <span className="ai-modal-toggle-slider" />
                      </label>
                    </div>
                  </div>

                  {/* Limit Overrides */}
                  <div className="ai-modal-section">
                    <div className="ai-modal-section-title">
                      <span className="ai-modal-section-dot" />
                      Limit Overrides
                    </div>
                    <p className="ai-modal-section-desc">
                      Set custom limits for this user. Leave blank to use the global defaults from AI settings.
                    </p>
                    <div className="ai-modal-limits-grid">
                      <div className="ai-modal-limit-field">
                        <label className="ai-modal-limit-label">
                          <FaChartBar />
                          Daily Query Limit
                        </label>
                        <input
                          type="number"
                          className="ai-modal-limit-input"
                          placeholder={`Global default (${globalDefaults.dailyLimit})`}
                          value={aiDailyLimit}
                          onChange={(e) => setAiDailyLimit(e.target.value)}
                          min="1"
                          disabled={aiBlocked}
                        />
                        <span className="ai-modal-limit-hint">
                          {aiDailyLimit === '' ? `Uses global default: ${globalDefaults.dailyLimit} per day` : `Override: ${aiDailyLimit} per day (global: ${globalDefaults.dailyLimit})`}
                        </span>
                      </div>
                      <div className="ai-modal-limit-field">
                        <label className="ai-modal-limit-label">
                          <FaTachometerAlt />
                          Per-Minute Rate Limit
                        </label>
                        <input
                          type="number"
                          className="ai-modal-limit-input"
                          placeholder={`Global default (${globalDefaults.rateLimitPerMinute})`}
                          value={aiRateLimit}
                          onChange={(e) => setAiRateLimit(e.target.value)}
                          min="1"
                          disabled={aiBlocked}
                        />
                        <span className="ai-modal-limit-hint">
                          {aiRateLimit === '' ? `Uses global default: ${globalDefaults.rateLimitPerMinute} / min` : `Override: ${aiRateLimit} / min (global: ${globalDefaults.rateLimitPerMinute})`}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="admin-modal__footer">
              <button
                className="admin-btn admin-btn--secondary"
                onClick={() => { setShowAiModal(false); setAiUser(null); setAiSettings(null); }}
                disabled={aiSettingsSaving}
              >
                Cancel
              </button>
              <button
                className={`admin-btn ${aiBlocked ? 'admin-btn--danger' : 'admin-btn--primary'}`}
                onClick={handleSaveAiSettings}
                disabled={aiSettingsSaving || aiSettingsLoading}
              >
                <FaSave /> {aiSettingsSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Modal */}
      {showPlanModal && selectedUser && (
        <div className="admin-modal-overlay" onClick={() => setShowPlanModal(false)}>
          <div className="admin-modal admin-modal--plan" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h3>Change User Plan</h3>
              <button className="admin-modal__close" onClick={() => setShowPlanModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal__body" style={{ overflowX: 'clip' }}>
              <div className="admin-plan-change">
                <div className="admin-plan-change__user">
                  <div className="admin-user-cell__avatar">
                    {selectedUser.photoURL ? (
                      <img src={selectedUser.photoURL} alt={selectedUser.name} />
                    ) : (
                      <span>{selectedUser.name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <div className="admin-plan-change__user-info">
                    <h4 title={selectedUser.name || 'Unnamed User'}>{selectedUser.name || 'Unnamed User'}</h4>
                    <p title={selectedUser.email}>{selectedUser.email}</p>
                  </div>
                </div>

                <div className="admin-plan-change__current">
                  <span className="admin-plan-change__current-label">Current Plan</span>
                  <div className={`admin-plan-badge admin-plan-badge--${selectedUser.plan || 'free'}`}>
                    {getPlanIcon(selectedUser.plan)}
                    {getPlanInfo(selectedUser.plan || 'free').name}
                  </div>
                  <button
                    type="button"
                    className="admin-btn admin-btn--outline admin-btn--small"
                    style={{ marginLeft: 'auto' }}
                    onClick={(e) => { e.stopPropagation(); setRecordUsageUser(selectedUser); }}
                    title="View record usage for this user"
                  >
                    <FaDatabase />
                    Records
                  </button>
                </div>

                <div className="admin-plan-change__dates">
                  <div className="admin-plan-change__date">
                    <div className="admin-plan-change__date-head">
                      <FaCalendarAlt />
                      <span>Start Date</span>
                    </div>
                    <strong className="admin-plan-change__date-value">{selectedUser.planStartsAt ? new Date(selectedUser.planStartsAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</strong>
                  </div>
                  <div className="admin-plan-change__date">
                    <div className="admin-plan-change__date-head">
                      <FaCalendarAlt />
                      <span>Expire Date</span>
                    </div>
                    <strong className="admin-plan-change__date-value">{selectedUser.planExpiresAt ? new Date(selectedUser.planExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</strong>
                  </div>
                </div>

                <div className="admin-plan-change__options">
                  <label>Select New Plan:</label>
                  <div className="admin-plan-options">
                    {PLANS.map((plan) => {
                      const currentPlan = selectedUser.plan || 'free';
                      const isCurrent = plan.id === currentPlan;
                      const isUpgrade = !isCurrent && PLANS.findIndex(p => p.id === plan.id) > PLANS.findIndex(p => p.id === currentPlan);
                      const isDowngrade = !isCurrent && PLANS.findIndex(p => p.id === plan.id) < PLANS.findIndex(p => p.id === currentPlan);
                      
                      return (
                        <div 
                          key={plan.id}
                          className={`admin-plan-option ${newPlan === plan.id ? 'admin-plan-option--selected' : ''} ${plan.id === currentPlan ? 'admin-plan-option--current' : ''}`}
                          onClick={() => setNewPlan(plan.id)}
                        >
                          {newPlan === plan.id && (
                            <div className="admin-plan-option__check" style={{ top: '8px', right: '8px' }}>
                              <FaCheckCircle />
                            </div>
                          )}
                          <div className="admin-plan-option__header">
                            <div className="admin-plan-option__icon" style={{ color: plan.color }}>
                              {plan.icon}
                            </div>
                            <div className="admin-plan-option__name">{plan.name}</div>
                            {isCurrent && <span className="admin-plan-option__badge admin-plan-option__badge--current">Current</span>}
                            {isUpgrade && <span className="admin-plan-option__badge admin-plan-option__badge--upgrade"><FaArrowUp /> Upgrade</span>}
                            {isDowngrade && <span className="admin-plan-option__badge admin-plan-option__badge--downgrade"><FaArrowDown /> Downgrade</span>}
                          </div>
                          <div className="admin-plan-option__price">
                            {plan.price > 0 ? `₹${getPlanYearlyPrice(plan.id).toLocaleString('en-IN')}/yr` : 'Free'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button 
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowPlanModal(false)}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn--outline"
                onClick={() => {
                  const user = selectedUser;
                  setShowPlanModal(false);
                  setSelectedUser(null);
                  setNewPlan('');
                  openExtendModal(user);
                }}
                disabled={updating}
              >
                <FaCalendarAlt /> Extend Days
              </button>
              <button
                className={`admin-btn admin-btn--small ${(selectedUser?.status === 'active') ? 'admin-btn--danger' : 'admin-btn--success'}`}
                onClick={async () => {
                  const newStatus = selectedUser?.status === 'active' ? 'inactive' : 'active';
                  const actionLabel = newStatus === 'inactive' ? 'Deactivate' : 'Activate';
                  const confirmed = await confirm(
                    `Are you sure you want to ${actionLabel.toLowerCase()} ${selectedUser?.name || 'this user'}?`,
                    { title: `Confirm ${actionLabel}`, confirmText: actionLabel, cancelText: 'Cancel' }
                  );
                  if (!confirmed) return;
                  await handleStatusChange(selectedUser._id, newStatus);
                  setShowPlanModal(false);
                }}
                disabled={updating}
              >
                {(selectedUser?.status === 'active') ? <><FaUserTimes /> Deactivate</> : <><FaUserCheck /> Activate</>}
              </button>
              <button 
                className="admin-btn admin-btn--primary"
                onClick={handlePlanChange}
                disabled={updating || newPlan === (selectedUser.plan || 'free')}
              >
                {updating ? 'Updating...' : 'Update Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Lightbox */}
      {previewImage && (
        <div
          className="admin-image-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <button
            className="admin-image-preview-close"
            onClick={() => setPreviewImage(null)}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          <img
            src={previewImage}
            alt="User avatar"
            className="admin-image-preview-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Record Usage Modal (same as Admin Security user log) */}
      {recordUsageUser && (
        <RecordUsageModal user={recordUsageUser} onClose={() => setRecordUsageUser(null)} />
      )}
    </div>
  );
};

export default PlanManagement;
